﻿import { useRef, useState } from 'react'
import { Check, Star, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import type { Task } from '@/types'
import { FREQUENCY_LABELS } from '@/types'
import { formatDueRemaining, formatNextScheduled, formatScheduledTime } from '@/lib/task-utils'
import { RATING_COLORS } from '@/lib/rating-colors'

type Props = {
  task: Task
  onComplete: (id: number) => void
  onDelete: (id: number) => void
  onEdit?: (task: Task) => void
  isCompleting?: boolean
  isSelecting?: boolean
  isSelected?: boolean
  onSelect?: (id: number) => void
  onLongPress?: () => void
}

export default function TaskCard({ task, onComplete, onDelete, onEdit, isCompleting, isSelecting, isSelected, onSelect, onLongPress }: Props) {
  const [open, setOpen] = useState(false)
  const [earlyOpen, setEarlyOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePointerDown = () => {
    if (!isSelecting && onLongPress) {
      longPressTimer.current = setTimeout(() => {
        onLongPress()
        onSelect?.(task.id)
      }, 500)
    }
  }
  const handlePointerUp = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }
  const isRecurringDone = task.frequency !== 'none' && !task.is_completable
  const isFutureStart = !task.is_completable && !!task.start_date && !isRecurringDone
  const nextLabel = isRecurringDone ? formatNextScheduled(task.next_scheduled_at, task.scheduled_time) : null
  const startLabel = isFutureStart ? formatNextScheduled(task.start_date + 'T00:00:00Z', task.scheduled_time) : null
  const dueLabel = task.due_date ? formatNextScheduled(task.due_date + 'T00:00:00Z', task.due_time ?? null) : null
  const dueRemaining = task.due_date ? formatDueRemaining(task.due_date) : null
  const repeatEndLabel = task.repeat_end_date ? formatNextScheduled(task.repeat_end_date + 'T00:00:00Z', null) : null
  const timeLabel = formatScheduledTime(task.scheduled_time)

  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-lg border transition-all cursor-pointer select-none
        ${isSelected ? 'bg-primary/10 border-primary' : 'bg-card active:bg-muted'}
        ${(isRecurringDone || isFutureStart) && !isSelecting ? 'opacity-50' : ''}
      `}
      onClick={() => {
        if (isSelecting) { onSelect?.(task.id); return }
        if (!open && !earlyOpen && !deleteOpen) onEdit?.(task)
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {isSelecting && (
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
        }`}>
          {isSelected && <Check size={12} className="text-primary-foreground" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{task.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {timeLabel && (
            <span className="text-xs text-muted-foreground">{timeLabel}</span>
          )}
          {task.frequency !== 'none' && (
            <span className="text-xs text-muted-foreground">{FREQUENCY_LABELS[task.frequency]}</span>
          )}
          {nextLabel && (
            <span className="text-xs text-muted-foreground">次回: {nextLabel}</span>
          )}
          {repeatEndLabel && (
            <span className="text-xs text-muted-foreground">〜{repeatEndLabel}</span>
          )}
          {startLabel && (
            <span className="text-xs text-muted-foreground">開始: {startLabel}</span>
          )}
          {dueLabel && (
            <span className="text-xs text-muted-foreground">期限: {dueLabel}</span>
          )}
          {dueRemaining && (
            <span className={`text-xs font-medium ${dueRemaining === '期限超過' ? 'text-destructive' : 'text-orange-500'}`}>
              {dueRemaining}
            </span>
          )}
          {task.intensity_value && (
            <span className="text-xs text-muted-foreground">
              {task.intensity_value} {task.intensity_unit}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={12}
                className={star <= task.rating ? RATING_COLORS.self : RATING_COLORS.empty}
              />
            ))}
          </div>
          {task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {task.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isSelecting && <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        {isRecurringDone ? (
          <Button size="sm" variant="outline" onClick={() => setEarlyOpen(true)} disabled={isCompleting}>
            先行完了
          </Button>
        ) : (
          <Button size="sm" onClick={() => setOpen(true)} disabled={isCompleting || !task.is_completable}>
            {task.is_completable ? '完了' : '完了済'}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setDeleteOpen(true)}
          className="bg-destructive/10 text-destructive hover:bg-destructive/20 px-2"
        >
          <Trash2 size={16} />
        </Button>
      </div>}

      <ConfirmDialog
        open={open} onOpenChange={setOpen}
        title={`「${task.title}」を完了しますか？`}
        confirmLabel="完了する"
        onConfirm={() => { setOpen(false); onComplete(task.id) }}
      />
      <ConfirmDialog
        open={earlyOpen} onOpenChange={setEarlyOpen}
        title={`「${task.title}」を先行完了しますか？`}
        confirmLabel="先行完了する"
        onConfirm={() => { setEarlyOpen(false); onComplete(task.id) }}
      />
      <ConfirmDialog
        open={deleteOpen} onOpenChange={setDeleteOpen}
        title={`「${task.title}」を削除しますか？`}
        confirmLabel="削除する"
        variant="destructive"
        onConfirm={() => { setDeleteOpen(false); onDelete(task.id) }}
      />
    </div>
  )
}
